import { API_ROUTES } from '@/constants/apiRoutes';
import {
  AddBusinessDocumentFields,
  BusinessDetails,
  BusinessDocumentResponse,
  BusinessesListResponse,
  BusinessFormFields,
  BusinessForSelectItem,
  MemberForm,
  MemberFormFields,
} from '@/types/businesses';
import { api, formatAddressForPost } from '@/utils/api';

export async function getBusinessesListService(queryParams: string) {
  const { data } = await api<BusinessesListResponse>({
    url: `${API_ROUTES.BUSINESSES}?${queryParams}`,
    method: 'get',
    version: 2,
  });

  return data;
}

export const getBusinessListForSelectionService = async (queryParams = '') => {
  const { data } = await api<BusinessForSelectItem[]>({
    url: `${API_ROUTES.BUSINESSES}for-selection?${queryParams}`,
    method: 'get',
    version: 2,
  });

  return data;
};

export async function getBusinessDetailsService(uuid: string) {
  const { data } = await api<BusinessDetails>({
    url: `${API_ROUTES.BUSINESSES}${uuid}`,
    method: 'get',
    version: 2,
  });

  return data;
}

function mapMembers(members: MemberFormFields[]) {
  const test = members.map(member => {
    return {
      consumer_id: member.consumer,
      ownership_percentage: member.ownershipPercentage,
      title: member.title,
      is_control_person: member.isControlPerson,
    };
  });
  return test;
}

export const addBusinessService = async (payload: BusinessFormFields) => {
  const currentAddress = formatAddressForPost(payload.currentAddress);
  const { data } = await api<string>({
    url: `${API_ROUTES.BUSINESSES}`,
    method: 'post',
    version: 2,
    data: {
      legal_business_name: payload.name,
      dba_name: payload.dbaName,
      legal_structure: payload.legalStructure,
      tin: payload.tin,
      confirm_tin: payload.confirmTin,
      company_id: payload.company,
      advisor_ids: payload.advisors,
      current_address: currentAddress,
      mailing_address: payload.sameMailingAddress
        ? currentAddress
        : formatAddressForPost(payload.mailingAddress),
      members: mapMembers(payload.members),
    },
  });

  return data;
};

export const editBusinessService = async (payload: BusinessFormFields) => {
  const currentAddress = formatAddressForPost(payload.currentAddress);
  const { data } = await api<string>({
    url: `${API_ROUTES.BUSINESSES}${payload.uuid}`,
    method: 'put',
    version: 2,
    data: {
      legal_business_name: payload.name,
      dba_name: payload.dbaName,
      legal_structure: payload.legalStructure,
      tin: payload.tin,
      confirm_tin: payload.confirmTin,
      advisor_ids: payload.advisors,
      current_address: currentAddress,
      mailing_address: payload.sameMailingAddress
        ? currentAddress
        : formatAddressForPost(payload.mailingAddress),
    },
  });

  return data;
};

export const editBusinessMembersService = async (payload: MemberForm) => {
  const { data } = await api<string>({
    url: `${API_ROUTES.BUSINESSES}${payload.uuid}/members`,
    method: 'put',
    version: 2,
    data: mapMembers(payload.members),
  });

  return data;
};

export const deactivateBusinessService = async (
  uuid: string,
  preflight: boolean,
) => {
  const { data } = await api<{ canDeactivate: boolean }>({
    url: `${API_ROUTES.BUSINESSES}${uuid}/deactivate`,
    method: 'post',
    version: 2,
    data: {
      preflight,
    },
  });

  return data;
};

export const getBusinessDocumentsService = async (uuid: string) => {
  const { data } = await api<BusinessDocumentResponse>({
    url: `${API_ROUTES.BUSINESSES}documents?business_id=${uuid}`,
    method: 'get',
    version: 2,
  });

  return data;
};

export const addBusinessDocumentsService = async (
  document: AddBusinessDocumentFields,
) => {
  const { data } = await api({
    url: `${API_ROUTES.BUSINESSES}document`,
    method: 'post',
    version: 2,
    data: {
      url: document.url,
      business_id: document.businessId,
      business_document_type: document.businessDocumentType,
    },
  });

  return data;
};

export const deleteBusinessDocumentsService = async (uuid: string) => {
  const { data } = await api({
    url: `${API_ROUTES.BUSINESSES}documents/${uuid}`,
    method: 'delete',
    version: 2,
  });

  return data;
};
